/**
 * PostHog experiment flag keys and variant types.
 *
 * Each experiment maps to a feature flag in PostHog configured as "Multivariate".
 * The variant union type must match the variants defined in the PostHog dashboard.
 *
 * Strategy: experiments test structural differences (layout, sequence).
 * Copy personalization uses message matching via utm_content, not random assignment.
 */

/**
 * Landing page layout experiment: chatbot-first vs traditional form.
 *
 * Research says AI chatbot flows convert 10-15% vs 2-3% for static forms.
 * This is the strategic bet for SA where WhatsApp dominates.
 * Two variants only: enough budget to reach significance at R70/day.
 */
export const DRIVER_LANDING_EXPERIMENT = 'driver-landing-variant';

export type DriverLandingVariant = 'control' | 'chatbot';

export const DRIVER_LANDING_VARIANTS: readonly DriverLandingVariant[] = [
  'control',
  'chatbot',
];

/**
 * Outreach sequence experiment: tests channel order and timing.
 *
 * Control: 7-step over 7 days (WA -> email -> SMS -> WA -> email -> SMS -> WA)
 * Compressed: 4-step in 24h (WA -> SMS -> WA -> email)
 *
 * Hypothesis: compressed 24h cadence outperforms spread 7-day for gig recruitment
 * where speed-to-engagement is the primary conversion driver.
 */
export const DRIVER_OUTREACH_EXPERIMENT = 'driver-outreach-variant';

export type DriverOutreachVariant = 'control' | 'compressed';

export const DRIVER_OUTREACH_VARIANTS: readonly DriverOutreachVariant[] = [
  'control',
  'compressed',
];

/**
 * Spitex supply-side outreach experiment: email-first vs WhatsApp-first.
 *
 * Control: email-first (4-step over 7 days, matches Swiss email preference)
 * WhatsApp-first: compressed 24h cadence, WhatsApp as initial touchpoint
 *
 * Hypothesis: WhatsApp-first may outperform email-first for Angehoerigenpflege
 * leads who are emotionally engaged and prefer personal messaging.
 * Start with 90/10 split (conservative), widen if WhatsApp wins.
 */
export const SPITEX_OUTREACH_EXPERIMENT = 'spitex-outreach-variant';

export type SpitexOutreachVariant = 'control' | 'whatsapp_first';

export const SPITEX_OUTREACH_VARIANTS: readonly SpitexOutreachVariant[] = [
  'control',
  'whatsapp_first',
];

/**
 * Registry mapping experiment flag keys to their valid variants.
 * Used by middleware and resolveExperimentVariant for generic validation.
 */
export const EXPERIMENT_REGISTRY: Record<string, readonly string[]> = {
  [DRIVER_LANDING_EXPERIMENT]: DRIVER_LANDING_VARIANTS,
  [DRIVER_OUTREACH_EXPERIMENT]: DRIVER_OUTREACH_VARIANTS,
  [SPITEX_OUTREACH_EXPERIMENT]: SPITEX_OUTREACH_VARIANTS,
};

/**
 * Message-match map: resolves landing page copy based on utm_content.
 *
 * Instead of A/B testing headlines randomly, we match the landing page
 * message to the ad creative that brought the user. Research shows
 * personalized CTAs produce +202% lift vs generic.
 *
 * Keys are utm_content values from Meta ad links.
 */
export const MESSAGE_MATCH: Record<string, { headline: string; cta: string }> =
  {
    // Earnings angle ads
    earnings_sipho: {
      headline: 'Start Earning Today',
      cta: 'Start Earning Now',
    },
    earnings_interest: {
      headline: 'Start Earning Today',
      cta: 'Start Earning Now',
    },
    // Speed angle ads
    speed: {
      headline: 'Apply Now. Drive by Friday.',
      cta: 'Apply in 2 Minutes',
    },
    // Social proof angle ads
    thandiwe: {
      headline: 'Join Thousands of SA Drivers',
      cta: 'See How They Did It',
    },
    david: {
      headline: 'Join Thousands of SA Drivers',
      cta: 'See How They Did It',
    },
  };

/**
 * Resolve headline and CTA copy based on utm_content from the ad.
 * Falls back to the vertical config defaults if no match.
 */
export function resolveMessageMatch(utmContent: string | undefined): {
  headline: string | undefined;
  cta: string | undefined;
} {
  if (!utmContent) return { headline: undefined, cta: undefined };

  const match = MESSAGE_MATCH[utmContent];

  if (!match) return { headline: undefined, cta: undefined };

  return match;
}
